import i18n from "i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSyncStatusStore } from "@/store/syncStatusStore";
import { useEditorStore } from "@/store/editorStore";
import { usePersonalityStore } from "@/store/personalityStore";
import {
  INITIAL_MOMENT_STATE,
  classifySyncTransition,
  nextBurstCheckAt,
  reduceMoment,
  type FullEvent,
  type MomentInput,
  type MomentKind,
  type MomentState,
  type SyncPhase,
} from "@/lib/savedMoment/momentRules";
import { pickFunStyle, type FunStyle } from "@/lib/savedMoment/funStyle";
import { VERSION_SAVED_EVENT, type VersionSavedDetail } from "@/lib/savedMoment/events";

/**
 * The save indicator's brain: what it shows at rest, and when it plays its
 * full moment or a tick. The rules themselves are the pure module
 * `momentRules`; this hook only feeds it the store's changes, the author's
 * keystrokes and the version events, with the time, and schedules the one
 * timer those rules need.
 *
 * It never subscribes to anything per keystroke that would re-render: the
 * keystroke feed is a store subscription, not a selector.
 */

/** What the indicator says at rest (`data-state`). */
export type SaveDisplay = "saved" | "saving" | "offline" | "failed" | "version" | "new";

export interface PlayedMoment {
  /** Increments per moment; keys the animated elements so each one replays. */
  id: number;
  kind: MomentKind;
  event?: FullEvent;
  /** The Fun style, for a full moment in Fun; null otherwise. */
  funStyle: FunStyle | null;
}

/** The full moment is 1.6 s; the tick 240 ms (handoff §1–2). */
export const FULL_MOMENT_MS = 1600;
export const TICK_MS = 240;
/** `version` holds its label this long, then returns to `saved` (§3). */
export const VERSION_HOLD_MS = 8000;
/** A failed save retries on its own this often (§3). */
export const AUTO_RETRY_MS = 10_000;
/** An edit this soon after a key, paste, drop or click is the author's. */
const AUTHOR_INPUT_WINDOW_MS = 1500;

const SYNC_TO_DISPLAY: Record<SyncPhase, SaveDisplay> = {
  upToDate: "saved",
  syncing: "saving",
  offline: "offline",
  error: "failed",
};

/** What a full moment announces (aria-live polite). Ticks are never announced. */
export function announcementFor(event: FullEvent, versionName: string | null): string {
  switch (event) {
    case "version":
      return versionName ? i18n.t("editor:saveState.versionNamedSaved", { name: versionName }) : i18n.t("editor:saveState.version");
    case "back-online":
      return i18n.t("editor:saveState.backOnline");
    default:
      return i18n.t("editor:saveState.allSaved");
  }
}

export interface SaveMoment {
  display: SaveDisplay;
  /** The moment playing now, or null at rest. */
  playing: PlayedMoment | null;
  /** The last moment played, kept after it ends (`data-moment`). */
  last: PlayedMoment | null;
  fullCount: number;
  tickCount: number;
  /** The held version's name while `display` is `version`. */
  versionName: string | null;
  /** The most recent version cut in this session, for the card. */
  lastVersion: { name: string; createdAt: string } | null;
  /** aria-live polite text; changes once per full moment. */
  politeMessage: string;
  lastSyncedAt: Date | null;
  pendingCount: number;
  detail: string | null;
  documentId: string | null;
  retry: () => void;
}

export function useSaveMoment(): SaveMoment {
  const state = useSyncStatusStore((s) => s.state);
  const lastSyncedAt = useSyncStatusStore((s) => s.lastSyncedAt);
  const version = useSyncStatusStore((s) => s.version);
  const detail = useSyncStatusStore((s) => s.detail);
  const documentId = useSyncStatusStore((s) => s.documentId);
  // Only while it is shown: selecting it always would re-render the bar on
  // every keystroke.
  const pendingCount = useSyncStatusStore((s) => (s.state === "offline" || s.state === "error" ? s.pendingCount : 0));
  const syncedMs = lastSyncedAt ? lastSyncedAt.getTime() : null;

  // Before the worker has seeded, the store holds its defaults: say what is
  // true of any document ("Saves as you type") rather than claim "Saved".
  const raw: SaveDisplay =
    documentId === null && state === "upToDate" && lastSyncedAt === null && version === 0
      ? "new"
      : SYNC_TO_DISPLAY[state];
  // A document none of the author's work has reached the server for yet.
  const [isNew, setIsNew] = useState(false);

  const [versionHold, setVersionHold] = useState<{ name: string; documentId: string } | null>(null);
  const [lastVersion, setLastVersion] = useState<{ name: string; createdAt: string } | null>(null);
  const [playing, setPlaying] = useState<PlayedMoment | null>(null);
  const [last, setLast] = useState<PlayedMoment | null>(null);
  const [counts, setCounts] = useState({ full: 0, tick: 0 });
  const [politeMessage, setPoliteMessage] = useState("");

  const momentRef = useRef<MomentState>(INITIAL_MOMENT_STATE);
  const seqRef = useRef(0);
  const lastFunRef = useRef<FunStyle | null>(null);
  const burstTimerRef = useRef<number | null>(null);
  const playTimerRef = useRef<number | null>(null);
  const politeTimerRef = useRef<number | null>(null);
  const versionNameRef = useRef<string | null>(null);

  const play = useCallback((kind: MomentKind, event?: FullEvent) => {
    const fun = usePersonalityStore.getState().personality === "fun";
    // Fun only styles FULL moments: ticks look the same in both modes.
    const funStyle = kind === "full" && fun && event ? pickFunStyle(event, lastFunRef.current, Math.random) : null;
    if (funStyle) lastFunRef.current = funStyle;
    const moment: PlayedMoment = { id: ++seqRef.current, kind, event, funStyle };
    setPlaying(moment);
    setLast(moment);
    setCounts((c) => (kind === "full" ? { ...c, full: c.full + 1 } : { ...c, tick: c.tick + 1 }));
    if (playTimerRef.current) window.clearTimeout(playTimerRef.current);
    playTimerRef.current = window.setTimeout(() => setPlaying(null), kind === "full" ? FULL_MOMENT_MS : TICK_MS);

    if (kind === "full" && event) {
      // Cleared first and set on the next frame, so the same words twice in a
      // row are still a change a screen reader will read.
      setPoliteMessage("");
      if (politeTimerRef.current) window.clearTimeout(politeTimerRef.current);
      const text = announcementFor(event, versionNameRef.current);
      politeTimerRef.current = window.setTimeout(() => setPoliteMessage(text), 50);
    }
  }, []);

  const scheduleBurstCheck = useCallback(() => {
    if (burstTimerRef.current) window.clearTimeout(burstTimerRef.current);
    burstTimerRef.current = null;
    const at = nextBurstCheckAt(momentRef.current);
    if (at === null) return;
    burstTimerRef.current = window.setTimeout(
      () => feedRef.current({ type: "burst-check", at: Date.now() }),
      Math.max(0, at - Date.now()),
    );
  }, []);

  const feed = useCallback(
    (input: MomentInput) => {
      const step = reduceMoment(momentRef.current, input);
      momentRef.current = step.state;
      if (step.decision.moment) play(step.decision.moment, step.decision.event);
      scheduleBurstCheck();
    },
    [play, scheduleBurstCheck],
  );
  const feedRef = useRef(feed);
  feedRef.current = feed;

  // ── Keystrokes: a store subscription, so typing never re-renders this ──
  //
  // An edit counts as the author's only when they just did something: the
  // editor also changes a document on its own as it settles (a new one's
  // first empty paragraph is normalised and saved on open), and a moment for
  // that would be a moment for nothing.
  const lastInputAtRef = useRef<number | null>(null);
  useEffect(() => {
    const mark = (e: Event) => {
      if (e.isTrusted) lastInputAtRef.current = Date.now();
    };
    const kinds = ["keydown", "beforeinput", "paste", "drop", "pointerup"] as const;
    for (const k of kinds) window.addEventListener(k, mark, { capture: true, passive: true });
    return () => {
      for (const k of kinds) window.removeEventListener(k, mark, { capture: true });
    };
  }, []);
  useEffect(
    () =>
      useEditorStore.subscribe(
        (s) => s.editVersion,
        (next, prev) => {
          // initDocument resets it to 0: that is a load, not typing.
          if (next <= prev) return;
          const input = lastInputAtRef.current;
          if (input === null || Date.now() - input > AUTHOR_INPUT_WINDOW_MS) return;
          feedRef.current({ type: "keystroke", at: Date.now() });
        },
      ),
    [],
  );

  // ── Sync store transitions ───────────────────────────────────────────
  const trackRef = useRef<{
    documentId: string | null;
    stable: SyncPhase;
    lastSyncedAt: number | null;
    neverSaved: boolean;
  }>({ documentId: null, stable: "upToDate", lastSyncedAt: null, neverSaved: false });

  useEffect(() => {
    const prev = trackRef.current;
    if (documentId !== prev.documentId) {
      // Another document (or the first): start over from what it is now. The
      // 60 s throttle is the person's, not the document's, so it carries.
      const neverSaved = documentId !== null && version === 0 && syncedMs === null;
      trackRef.current = {
        documentId,
        stable: state === "syncing" ? "upToDate" : state,
        lastSyncedAt: syncedMs,
        neverSaved,
      };
      setIsNew(neverSaved);
      lastInputAtRef.current = null;
      momentRef.current = { ...INITIAL_MOMENT_STATE, lastFullAt: momentRef.current.lastFullAt };
      setCounts({ full: 0, tick: 0 });
      setLast(null);
      setPlaying(null);
      setVersionHold(null);
      setLastVersion(null);
      scheduleBurstCheck();
      return;
    }
    if (documentId === null) return;

    const pending = useSyncStatusStore.getState().pendingCount;
    const authorTyped = momentRef.current.lastKeystrokeAt !== null;
    const t = classifySyncTransition(
      prev.stable,
      { state, lastSyncedAt: syncedMs, pendingCount: pending },
      prev.lastSyncedAt,
      prev.neverSaved,
      authorTyped,
    );
    const savedNow = syncedMs !== null && syncedMs !== prev.lastSyncedAt;
    // `new` ends when the author's own work reaches the server — not when the
    // editor saves the empty document it opened.
    const neverSaved = prev.neverSaved && !(savedNow && authorTyped);
    if (neverSaved !== prev.neverSaved) setIsNew(neverSaved);
    const at = Date.now();
    if (t?.type === "qualifying") feed({ type: "qualifying", event: t.event, at });
    else if (t?.type === "save-completed") feed({ type: "save-completed", at, pending });

    trackRef.current = {
      documentId,
      // `syncing` is a pass-through: a slow save out of `error` is still a
      // recovery when it lands.
      stable: state === "syncing" ? prev.stable : state,
      lastSyncedAt: syncedMs,
      // The first save patches the version before it stamps lastSyncedAt, so
      // "never saved" holds until the stamp, not until the version moves.
      neverSaved,
    };
  }, [documentId, state, syncedMs, version, feed, scheduleBurstCheck]);

  // ── Named versions ───────────────────────────────────────────────────
  useEffect(() => {
    const onVersion = (e: Event) => {
      const d = (e as CustomEvent<VersionSavedDetail>).detail;
      const current = useSyncStatusStore.getState().documentId;
      if (!d || (current && d.documentId !== current)) return;
      versionNameRef.current = d.name;
      setVersionHold({ name: d.name, documentId: d.documentId });
      setLastVersion({ name: d.name, createdAt: d.createdAt });
      feedRef.current({ type: "qualifying", event: "version", at: Date.now() });
    };
    window.addEventListener(VERSION_SAVED_EVENT, onVersion);
    return () => window.removeEventListener(VERSION_SAVED_EVENT, onVersion);
  }, []);

  useEffect(() => {
    if (!versionHold) return;
    const t = window.setTimeout(() => setVersionHold(null), VERSION_HOLD_MS);
    return () => window.clearTimeout(t);
  }, [versionHold]);

  const display: SaveDisplay =
    versionHold && (raw === "saved" || raw === "saving")
      ? "version"
      : isNew && raw === "saved"
        ? "new"
        : raw;

  // ── Failed: retry on its own every 10 s ──────────────────────────────
  const retry = useCallback(() => {
    window.dispatchEvent(new CustomEvent("lilia:save-now"));
  }, []);
  useEffect(() => {
    if (display !== "failed") return;
    const t = window.setInterval(retry, AUTO_RETRY_MS);
    return () => window.clearInterval(t);
  }, [display, retry]);

  // ── Timers die with the component ────────────────────────────────────
  useEffect(
    () => () => {
      for (const r of [burstTimerRef, playTimerRef, politeTimerRef]) {
        if (r.current) window.clearTimeout(r.current);
      }
    },
    [],
  );

  return {
    display,
    playing,
    last,
    fullCount: counts.full,
    tickCount: counts.tick,
    versionName: display === "version" ? versionHold?.name ?? null : null,
    lastVersion,
    politeMessage,
    lastSyncedAt,
    pendingCount,
    detail,
    documentId,
    retry,
  };
}
